import { useCallback, useMemo } from 'react'

import { useAccount } from 'wagmi'

import { Token, Strategies, AirdropList, Params, GasParamsStringType } from 'types/entities'

import { AppState } from 'state'
import { useAppDispatch, useAppSelector } from 'state/hooks'

import { useAirdropper } from 'hooks/airdrop'

import { Airdropper } from 'services/airdropper'

import { compareAddresses, toChecksumAddress, toWei } from 'utils/crypto'

import { errors } from 'constants/errors'
import { etherNumbers } from 'constants/variables'
import { AirdropStatus } from 'constants/transaction'

import {
  setToken,
  setTxsList,
  setStrategy,
  setSignature,
  setGasParams,
  setBatchSize,
  setAirdropId,
  clearTokensList,
  setDeflationary,
  setAirdropStatus,
  setRecipientsList,
  setAirdropChainId,
  setExceptionsList,
  clearAirdropParams,
  clearPrepareAirdropData,
} from './slice'

import { StoredAirdropList, SetAirdropParamsInput, TxParams, Signature } from './types'

export function useIsSelfGenerated() {
  const strategy = useStrategy()
  return useMemo(() => strategy === Strategies.SELF_GENERATED, [strategy])
}

export function useAirdropList() {
  const token = useToken()
  const recipientsList = useRecipientsList()
  const exceptionsList = useExceptionsList()

  const findException = (address: `0x${string}`) => {
    return exceptionsList.some((el) => compareAddresses(el.addr, address))
  }

  const numDecimals = Number(token?.decimals.toString())

  return useMemo(() => {
    return recipientsList.reduce<AirdropList>((acc, curr) => {
      try {
        const isException = exceptionsList.length > 0 && findException(curr.addr)
        if (!isException) {
          acc.push({ addr: toChecksumAddress(curr.addr), amount: toWei(curr.amount, numDecimals) })
          return acc
        }
      } catch {
        return acc
      }

      return acc
    }, [])
  }, [recipientsList, exceptionsList, token, numDecimals])
}

export function useBigintExceptionsList() {
  const exceptionsList = useExceptionsList()
  return useMemo(() => exceptionsList.map((el) => ({ ...el, amount: BigInt(el.amount) })), [exceptionsList])
}

export function useTotalGasLimit() {
  const txs = useTxsList()

  return useMemo(() => {
    return txs.reduce((acc, curr) => {
      return curr.gas ? acc + BigInt(curr.gas) : acc
    }, etherNumbers.BG_ZERO)
  }, [txs])
}

export function useTotalTokens() {
  const airdropList = useAirdropList()
  return useMemo(() => {
    return airdropList.reduce(Airdropper.getTotalAmount, etherNumbers.BG_ZERO)
  }, [airdropList])
}

export function useIsAirdropSuccess() {
  const airdropStatus = useAirdropStatus()

  return useMemo(() => {
    return airdropStatus === AirdropStatus.SUCCESS
  }, [airdropStatus])
}

export function useIsAirdropFailed() {
  const airdropStatus = useAirdropStatus()

  return useMemo(() => {
    return airdropStatus === AirdropStatus.FAILED
  }, [airdropStatus])
}

export function useIsAirdropPending() {
  const airdropStatus = useAirdropStatus()

  return useMemo(() => {
    return airdropStatus === AirdropStatus.PENDING
  }, [airdropStatus])
}

export function useIsAirdropNotStarted() {
  const airdropStatus = useAirdropStatus()

  return useMemo(() => {
    return airdropStatus === AirdropStatus.NOT_STARTED
  }, [airdropStatus])
}

export function useIsAirdropInterrupted() {
  const airdropStatus = useAirdropStatus()

  return useMemo(() => {
    return airdropStatus === AirdropStatus.INTERRUPTED
  }, [airdropStatus])
}

export function useTransactionsCount() {
  const batchSize = useBatchSize()
  const airdropList = useAirdropList()

  return useMemo(() => {
    return Math.ceil(airdropList.length / Number(batchSize))
  }, [airdropList.length, batchSize])
}

export function useSetAirdropParams() {
  const setBatchSize = useSetBatchSize()
  const setDeflationary = useSetDeflationary()
  const setExceptionsList = useSetExceptionsList()

  return useCallback(
    ({ batchSize, isDeflationary, exceptions }: SetAirdropParamsInput) => {
      setBatchSize(batchSize.toString())
      setDeflationary(Boolean(isDeflationary))
      setExceptionsList(exceptions.map((el) => ({ ...el, amount: el.amount.toString() })))
    },
    [setBatchSize, setDeflationary, setExceptionsList],
  )
}

export function useAirdropParams() {
  const token = useToken()
  const strategy = useStrategy()
  const batchSize = useBatchSize()
  const airdropList = useAirdropList()
  const isDeflationary = useIsDeflationary()
  const airdropChainId = useAirdropChainId()

  return useMemo(
    () => ({
      token,
      strategy,
      airdropList,
      isDeflationary,
      airdropChainId,
      batchSize: BigInt(batchSize),
    }),
    [token, strategy, batchSize, airdropList, isDeflationary, airdropChainId],
  )
}

export function useSetRecipientsList() {
  const dispatch = useAppDispatch()

  return useCallback(
    (data: StoredAirdropList) => {
      dispatch(setRecipientsList(data))
    },
    [dispatch],
  )
}

export function useSetExceptionsList() {
  const dispatch = useAppDispatch()

  return useCallback(
    (data: StoredAirdropList) => {
      dispatch(setExceptionsList(data))
    },
    [dispatch],
  )
}

export function useSetSignature() {
  const dispatch = useAppDispatch()

  return useCallback(
    (data: Signature) => {
      dispatch(setSignature(data))
    },
    [dispatch],
  )
}

export function useSetToken() {
  const dispatch = useAppDispatch()

  return useCallback(
    (token: Token | null) => {
      dispatch(setToken(token))
    },
    [dispatch],
  )
}

export function useSetBatchSize() {
  const dispatch = useAppDispatch()

  return useCallback(
    (batchSize: string) => {
      dispatch(setBatchSize(batchSize))
    },
    [dispatch],
  )
}

export function useSetDeflationary() {
  const dispatch = useAppDispatch()

  return useCallback(
    (isDeflationary: boolean) => {
      dispatch(setDeflationary(isDeflationary))
    },
    [dispatch],
  )
}

export function useSetAirdropChainId() {
  const dispatch = useAppDispatch()

  return useCallback(
    (chainId: SupportedChainId) => {
      dispatch(setAirdropChainId(chainId))
    },
    [dispatch],
  )
}

export function useSetStrategy() {
  const dispatch = useAppDispatch()

  return useCallback(
    (strategy: Strategies) => {
      dispatch(setStrategy(strategy))
    },
    [dispatch],
  )
}

export function useSetAirdropId() {
  const dispatch = useAppDispatch()

  return useCallback(
    (id: string) => {
      dispatch(setAirdropId(id))
    },
    [dispatch],
  )
}

export function useSetTxsList() {
  const dispatch = useAppDispatch()

  return useCallback(
    (txsList: TxParams[]) => {
      dispatch(setTxsList(txsList))
    },
    [dispatch],
  )
}

export function usePrepareTxsList() {
  const { address: customer } = useAccount()

  const params = useAirdropParams()
  const airdropper = useAirdropper()

  const setTxsList = useSetTxsList()

  return useCallback(async () => {
    if (!customer) {
      throw new Error(errors.wallet.WALLET_REQUIRED)
    }

    if (!params.token) {
      throw new Error(errors.validation.TOKEN_IS_REQUIRED)
    }

    try {
      const txs: Params[] = await airdropper.createAirdropTxs({
        customer,
        token: params.token,
        batchSize: params.batchSize,
        airdropList: params.airdropList,
        isDeflationary: params.isDeflationary,
      })

      // ToDo write formatter
      setTxsList(
        txs.map((tx) => ({
          to: tx.to,
          from: tx.from,
          data: tx.data,
          gas: tx.gas?.toString(),
          value: tx.value.toString(),
        })),
      )

      return txs
    } catch (err) {
      throw err
    }
  }, [customer, params, airdropper])
}

export function useSetGasParams() {
  const dispatch = useAppDispatch()

  return useCallback(
    (gasParams: GasParamsStringType) => {
      dispatch(setGasParams(gasParams))
    },
    [dispatch],
  )
}

export function useSetAirdropStatus() {
  const dispatch = useAppDispatch()

  return useCallback(
    (status: AirdropStatus) => {
      dispatch(setAirdropStatus(status))
    },
    [dispatch],
  )
}

export function useClearState() {
  const dispatch = useAppDispatch()

  return useCallback(() => {
    dispatch(clearTokensList())
  }, [dispatch])
}

export function useClearPrepareAirdropData() {
  const dispatch = useAppDispatch()

  return useCallback(() => {
    dispatch(clearPrepareAirdropData())
  }, [dispatch])
}

export function useClearAirdropParams() {
  const dispatch = useAppDispatch()

  return useCallback(() => {
    dispatch(clearAirdropParams())
  }, [dispatch])
}

export function useToken() {
  return useAppSelector((state: AppState) => state.airdrop.token)
}

export function useSignature() {
  return useAppSelector((state) => state.airdrop.signature)
}

export function useRecipientsList() {
  return useAppSelector((state: AppState) => state.airdrop.recipientsList)
}

export function useExceptionsList() {
  return useAppSelector((state: AppState) => state.airdrop.exceptionsList)
}

export function useBatchSize() {
  return useAppSelector((state: AppState) => state.airdrop.batchSize)
}

export function useIsDeflationary() {
  return useAppSelector((state: AppState) => state.airdrop.isDeflationary)
}

export function useAirdropChainId() {
  return useAppSelector((state: AppState) => state.airdrop.chainId)
}

export function useAirdropId() {
  return useAppSelector((state: AppState) => state.airdrop.airdropId)
}

export function useStrategy() {
  return useAppSelector((state: AppState) => state.airdrop.strategy)
}

export function useAirdropStatus() {
  return useAppSelector((state: AppState) => state.airdrop.airdropStatus)
}

export function useTxsList() {
  return useAppSelector((state: AppState) => state.airdrop.txsList)
}

export function useGasParams() {
  return useAppSelector((state: AppState) => state.airdrop.gasParams)
}
